import { useMemo } from 'react';

/**
 * Hook para calcular e formatar valores de agendamentos e serviços
 * Implementa padrões consistentes para acesso às propriedades de valor no modelo de dados
 * 
 * @returns {Object} Objeto com funções para calcular e formatar valores
 */
export const useAppointmentValues = () => {
  /**
   * Calcula o valor total de um agendamento com base em seus serviços
   * 
   * @param {Object} appointment - Objeto de agendamento
   * @returns {number} Valor total calculado
   */
  const calculateTotalValue = (appointment) => {
    if (!appointment) return 0;
    
    // Primeiro verifica se já existe um valor total definido
    if (appointment.valorTotal && typeof appointment.valorTotal === 'number') {
      return appointment.valorTotal;
    }
    
    // Caso não tenha valorTotal, calcula com base nos serviços
    if (Array.isArray(appointment.servicos) && appointment.servicos.length > 0) {
      return appointment.servicos.reduce((total, servico) => {
        // Verificar todas as propriedades possíveis onde o valor pode estar armazenado
        const valor = servico.valor || servico.preco || 0;
        return total + valor;
      }, 0);
    }
    
    // Se tiver servicosDetalhes, calcula com base neles
    if (Array.isArray(appointment.servicosDetalhes) && appointment.servicosDetalhes.length > 0) {
      return appointment.servicosDetalhes.reduce((total, servico) => {
        const valor = servico.preco || servico.valor || 0;
        return total + valor;
      }, 0);
    }
    
    return 0;
  };
  
  /**
   * Formata um valor numérico para o formato de moeda brasileira
   * 
   * @param {number} value - Valor a ser formatado
   * @returns {string} Valor formatado (ex: "R$ 99,90")
   */
  const formatCurrency = (value) => {
    if (value === undefined || value === null) return 'R$ 0,00';
    
    return `R$ ${Number(value).toFixed(2).replace('.', ',')}`;
  };
  
  /**
   * Formata o valor total de um agendamento
   * 
   * @param {Object} appointment - Objeto de agendamento
   * @returns {string} Valor formatado (ex: "R$ 99,90")
   */
  const formatAppointmentValue = (appointment) => {
    if (!appointment) return 'R$ 0,00';
    
    const total = calculateTotalValue(appointment);
    return formatCurrency(total);
  };
  
  return {
    calculateTotalValue,
    formatCurrency,
    formatAppointmentValue
  };
};

export default useAppointmentValues; 